import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { SectionLabel, PanelCard } from '../primitives';
import { apiFetch } from '../../lib/api';
import { SuburbField } from '../SuburbField';
import { ProfitLossTab } from './ProfitLossTab';
import { SwmsRecordDialog } from './SwmsRecordDialog';
import { formatShort } from '../../lib/dates';
import type { Job, JobDetail, SwmsTask } from '../../hooks/useJobs';

/**
 * The job form: Job Details, Profit & Loss, SWMS Job Template, SWMS Employee
 * Records — the same four tabs as jobs_detail_view.php, over one fetch.
 *
 * Renders the title, body and actions of a Dialog; the caller owns the <Dialog>.
 * Laid out like click2gro's GeneralTaskEditDialog.
 */

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

interface JobFormProps {
    readonly detail: JobDetail;
    readonly tasks: readonly SwmsTask[];
    readonly clients: readonly { id: number; companyName: string }[];
    readonly onSaved: () => void;
    readonly onDeleted: () => void;
    readonly onClose: () => void;
}

export function JobForm({ detail, tasks, clients, onSaved, onDeleted, onClose }: JobFormProps): React.JSX.Element {
    const [tab, setTab] = useState(0);
    const [form, setForm] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        setForm({});
        setError(null);
        setConfirmDelete(false);
    }, [detail]);

    const value = (key: keyof Job): string => {
        const override = form[key];
        if (override !== undefined) {
            return override;
        }
        const v = detail.job[key];
        return v === null || v === undefined ? '' : String(v);
    };

    const set = (key: keyof Job) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    const dirty = Object.keys(form).length > 0;

    function save(): void {
        setSaving(true);
        setError(null);
        const body: Record<string, unknown> = { ...form };
        for (const key of ['clientId', 'budgetCost', 'costMaterials', 'jobNumber']) {
            if (typeof body[key] === 'string') {
                body[key] = body[key] === '' ? null : Number(body[key]);
            }
        }
        apiFetch<Job>(`/jobs/${String(detail.job.id)}`, { method: 'PATCH', body: JSON.stringify(body) })
            .then(() => {
                onSaved();
                onClose();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function remove(): void {
        setSaving(true);
        apiFetch<unknown>(`/jobs/${String(detail.job.id)}`, { method: 'DELETE' })
            .then(onDeleted)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not delete');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                Edit Job {detail.job.jobNumber}
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                    {[detail.job.jobAddress, detail.job.jobSuburb].filter((p) => p !== null && p !== '').join(' ')}
                </Typography>
                <Chip
                    size="small"
                    label={detail.job.status}
                    color={detail.job.status === 'Active' ? 'success' : 'default'}
                    variant="outlined"
                />
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={onClose} size="small" disabled={saving} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: '60vh' }}>
                {error !== null ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                <Tabs
                    value={tab}
                    onChange={(_e, v: number) => {
                        setTab(v);
                    }}
                    variant="scrollable"
                    sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Tab label="Job Details" />
                    <Tab label="Profit & Loss" />
                    <Tab label={`SWMS Template (${String(detail.swmsTaskIds.length)})`} />
                    <Tab label={`SWMS Records (${String(detail.signoffs.length)})`} />
                </Tabs>

                {/* ─── Job Details ────────────────────────────────────────────── */}
                {tab === 0 ? (
                    <Stack gap={2.5}>
                        <PanelCard>
                            <SectionLabel>Job</SectionLabel>
                            <Stack gap={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        label="Registered date"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={value('jobRegisterDate')}
                                        onChange={set('jobRegisterDate')}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                    />
                                    <TextField
                                        label="Job number"
                                        size="small"
                                        fullWidth
                                        value={value('jobNumber')}
                                        onChange={set('jobNumber')}
                                    />
                                    <TextField
                                        label="Job code"
                                        size="small"
                                        fullWidth
                                        value={value('jobCode')}
                                        onChange={set('jobCode')}
                                    />
                                    <TextField
                                        select
                                        label="Status"
                                        size="small"
                                        fullWidth
                                        value={value('status')}
                                        onChange={set('status')}
                                    >
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="Inactive">Inactive</MenuItem>
                                    </TextField>
                                </Stack>
                                {/* The legacy column was a client NAME string, which is
                                why 'Firma Construction Group' and a bare '15' both
                                appear in it. */}
                                <TextField
                                    select
                                    label="Client"
                                    size="small"
                                    value={value('clientId')}
                                    onChange={set('clientId')}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {clients.map((c) => (
                                        <MenuItem key={c.id} value={String(c.id)}>
                                            {c.companyName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Site</SectionLabel>
                            <Stack gap={1.5}>
                                <TextField
                                    label="Address"
                                    size="small"
                                    value={value('jobAddress')}
                                    onChange={set('jobAddress')}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <SuburbField
                                        value={value('jobSuburb')}
                                        onChange={(v) => {
                                            setForm((f) => ({ ...f, jobSuburb: v }));
                                        }}
                                        onSelect={(m) => {
                                            setForm((f) => ({
                                                ...f,
                                                jobSuburb: m.suburb,
                                                jobPostcode: m.postcode,
                                                jobState: m.state,
                                            }));
                                        }}
                                    />
                                    <TextField
                                        label="Postcode"
                                        size="small"
                                        sx={{ width: { sm: 140 } }}
                                        value={value('jobPostcode')}
                                        onChange={set('jobPostcode')}
                                    />
                                    <TextField
                                        select
                                        label="State"
                                        size="small"
                                        sx={{ width: { sm: 120 } }}
                                        value={value('jobState')}
                                        onChange={set('jobState')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {STATES.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {s}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Site contact</SectionLabel>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <TextField
                                    label="Name"
                                    size="small"
                                    fullWidth
                                    value={value('contactName')}
                                    onChange={set('contactName')}
                                />
                                <TextField
                                    label="Phone"
                                    size="small"
                                    fullWidth
                                    value={value('contactPhone')}
                                    onChange={set('contactPhone')}
                                />
                                <TextField
                                    label="Email"
                                    size="small"
                                    fullWidth
                                    value={value('contactEmail')}
                                    onChange={set('contactEmail')}
                                />
                            </Stack>
                        </PanelCard>
                    </Stack>
                ) : null}

                {/* ─── Profit & Loss ──────────────────────────────────────────── */}
                {tab === 1 ? <ProfitLossTab detail={detail} value={value} set={set} onChanged={onSaved} /> : null}

                {/* ─── SWMS Job Template ──────────────────────────────────────── */}
                {tab === 2 ? (
                    <SwmsTemplateTab
                        jobId={detail.job.id}
                        tasks={tasks}
                        selectedIds={detail.swmsTaskIds}
                        onSaved={onSaved}
                    />
                ) : null}

                {/* ─── SWMS Employee Records ──────────────────────────────────── */}
                {tab === 3 ? <SignoffTab detail={detail} /> : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {confirmDelete ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mr: 'auto' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>Delete this job?</Typography>
                        <Button size="small" color="error" variant="contained" onClick={remove} disabled={saving}>
                            Delete
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setConfirmDelete(false);
                            }}
                        >
                            Keep
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                            setConfirmDelete(true);
                        }}
                        disabled={saving}
                        sx={{ mr: 'auto' }}
                    >
                        Delete Job
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!dirty || saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Update Job'}
                </Button>
            </DialogActions>
        </>
    );
}

// ─── SWMS Job Template ──────────────────────────────────────────────────────

interface SwmsTemplateTabProps {
    readonly jobId: number;
    readonly tasks: readonly SwmsTask[];
    readonly selectedIds: readonly number[];
    readonly onSaved: () => void;
}

function SwmsTemplateTab({ jobId, tasks, selectedIds, onSaved }: SwmsTemplateTabProps): React.JSX.Element {
    const [selected, setSelected] = useState<ReadonlySet<number>>(new Set(selectedIds));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setSelected(new Set(selectedIds));
    }, [selectedIds]);

    const dirty = useMemo(() => {
        if (selected.size !== selectedIds.length) {
            return true;
        }
        return selectedIds.some((id) => !selected.has(id));
    }, [selected, selectedIds]);

    function toggle(id: number): void {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }

    function save(): void {
        setSaving(true);
        setError(null);
        apiFetch<unknown>(`/jobs/${String(jobId)}/swms-tasks`, {
            method: 'PUT',
            body: JSON.stringify({ taskIds: [...selected] }),
        })
            .then(onSaved)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <PanelCard>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel divider={false}>SWMS tasks for this job</SectionLabel>
                </Box>
                <Button size="small" variant="contained" onClick={save} disabled={!dirty || saving}>
                    Save selection
                </Button>
            </Stack>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Task</TableCell>
                        <TableCell>Hazards &amp; risks</TableCell>
                        <TableCell>Control measures</TableCell>
                        <TableCell align="right">Risk</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tasks.map((t) => (
                        <TableRow
                            key={t.id}
                            hover
                            selected={selected.has(t.id)}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                toggle(t.id);
                            }}
                        >
                            <TableCell padding="checkbox">
                                <Checkbox size="small" checked={selected.has(t.id)} tabIndex={-1} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{t.task}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                                {t.hazardsRisks}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                                {t.controlMeasures}
                            </TableCell>
                            <TableCell align="right">
                                {/* Colour comes from the level, not from a stored
                                    hex — the legacy colour columns were NULL on
                                    all 20 tasks, and a hand-set colour is how a
                                    Major task ends up looking minor. */}
                                {t.riskLevel === null ? null : (
                                    <Chip
                                        size="small"
                                        color={t.riskLevel === 1 ? 'error' : t.riskLevel === 2 ? 'warning' : 'success'}
                                        label={t.riskLevel === 1 ? 'Major' : t.riskLevel === 2 ? 'Moderate' : 'Minor'}
                                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                                    />
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {tasks.length === 0 ? (
                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled', mt: 1 }}>
                    No SWMS tasks in the library yet.
                </Typography>
            ) : null}
        </PanelCard>
    );
}

// ─── SWMS Employee Records ──────────────────────────────────────────────────

function SignoffTab({ detail }: { detail: JobDetail }): React.JSX.Element {
    const [viewing, setViewing] = useState<number | null>(null);

    return (
        <PanelCard>
            <SectionLabel>Who has signed the SWMS</SectionLabel>

            {detail.signoffs.length === 0 ? (
                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
                    Nobody has signed off on this job yet.
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Staff member</TableCell>
                            <TableCell>Person responsible</TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {detail.signoffs.map((s) => (
                            <TableRow key={s.id} hover>
                                <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                    {formatShort(s.signedAt.slice(0, 10))}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{s.staffName}</TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{s.personResponsible}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon fontSize="small" />}
                                        onClick={() => {
                                            setViewing(s.id);
                                        }}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <SwmsRecordDialog
                jobId={detail.job.id}
                recordId={viewing}
                onClose={() => {
                    setViewing(null);
                }}
            />
        </PanelCard>
    );
}
